/* eslint-disable max-lines */
import { datadogRum } from '@datadog/browser-rum-slim'

import { Product } from '@evenfinancial/even-ts-static'
import { LILeadApiError } from '../components/LifeInsurance/errors'
export type ErrorType = string | Error | ErrorEvent

export const captureTrackingImpressionError = (
  asset: string,
  companyUuid: string,
  leadUuid: string,
  referrer: string,
  error: ErrorType,
) => {
  datadogRum.addError(error, {
    asset,
    companyUuid,
    leadUuid,
    message: 'Tracking Impression Failed',
    referrer,
  })
}

export const captureGetCombinedConfigError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'getCombinedConfig failure',
  })
}

export const captureGetPartnerConfigError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'getPartnerConfig failure',
  })
}

export const captureTrackingAppnexusIdWarning = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Appnexus ID Capture Failed',
  })
}

export const captureGoogleMapsError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Google Maps Client Request Failed',
  })
}

export const captureCookieCapabilityError = () => {
  const error = new Error('Cookie Storage Failed')
  datadogRum.addError(error, undefined)
}

export const captureTrackingEventError = (action: string, trackingUuid: string, error: ErrorType) => {
  datadogRum.addError(error, {
    action,
    message: 'Tracking Event Failed',
    trackingUuid,
  })
}

export const captureTrackingWithNoUuidError = (action: string, error: ErrorType) => {
  datadogRum.addError(error, {
    action,
    message: 'Tracking Event with No UUID',
  })
}

export const capturePartnerConfigError = (partnerApiUrl: string, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Get Partner Config Failed',
    partnerApiUrl,
  })
}

export const captureParentDataError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Get Parent Data Failed',
  })
}

export const captureFetchPreviewOffersError = (product: Product, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Error fetching loan preview offers',
    product,
  })
}

export const captureGeocoderError = (params: object, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Geocoder Failed',
    params,
  })
}

export const captureRefreshRateTablesError = (product: Product, rateTableUuid: string, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Rate Table Refresh Failed',
    product,
    rateTableUuid,
  })
}

export const captureGetStaticOfferError = (product: Product, offerUuid: string, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Get Static Offer Failed',
    offerUuid,
    product,
  })
}

export const captureGetRateTablesError = (product: Product, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Get Rate Tables Failed',
    product,
  })
}

export const captureGeolocateError = (product: Product, error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Geolocate from IP Failed',
    product,
  })
}

export const captureLeadApiError = <T extends Error>(product: Product, leadUuid: string, error: T) => {
  if (error instanceof LILeadApiError) {
    datadogRum.addError(error, {
      leadUuid: error.leadUuid,
      message: error.message,
      method: error.method,
      product,
    })
  } else {
    datadogRum.addError(error, {
      leadUuid,
      message: error.message,
      product,
    })
  }
}

export const captureNoApiTokenWarning = () => {
  const error = new Error('No access token in URL Params')
  datadogRum.addError(error, undefined)
}

export const captureNoCompanyUuidWarning = () => {
  const error = new Error('No company uuid in URL Params')
  datadogRum.addError(error, undefined)
}

export const capturePostMessageWarning = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Message to Helper Failed',
  })
}

export const captureQueryParamParseError = (value: string, key: string) => {
  const error = new Error('Incorrectly formatted query param')
  datadogRum.addError(error, {
    key,
    value,
  })
}

export const captureZeroOffersWarning = (rateTableUuid: string, product: Product) => {
  const error = new Error('Returned 0 offers')
  datadogRum.addError(error, {
    product,
    rateTableUuid,
  })
}

export const captureHelperTimeout = () => {
  const error = new Error('Helper timed out finding iframe')
  datadogRum.addError(error, undefined)
}

export const captureCantSendHelperMessage = (iframeSrc: string) => {
  const error = new Error('Could not send Helper message')
  datadogRum.addError(error, {
    href: window.location.href,
    iframeSrc,
  })
}

export const captureNoChildElementWarning = () => {
  const error = new Error('Could not find child element')
  datadogRum.addError(error, {
    href: window.location.href,
  })
}

export const captureInvalidPartnerConfigResponseWarning = (response: unknown) => {
  const error = new Error('Invalid partner config response')
  datadogRum.addError(error, {
    response,
  })
}

export const captureErrorOpeningWindowFromHelperEvent = (url: string) => {
  const error = new Error('Error opening window from helper script')
  datadogRum.addError(error, {
    url,
  })
}

export const captureErrorOpeningWindowFromEmbedEvent = (url: string) => {
  const error = new Error('Error opening window from embed')
  datadogRum.addError(error, {
    url,
  })
}

export const captureUniversityRequestError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'University List Request Failed',
  })
}

export const capturePrescriptionRequestError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Prescription List Request Failed',
  })
}

export const captureMultipleProductSubTypeRateTable = (uuid: string) => {
  const error = new Error('RateTable with Multiple ProductSubTypes')
  datadogRum.addError(error, {
    rateTableUuid: uuid,
  })
}

export const captureInvalidProductStepUpdate = (step) => {
  const error = new Error('Invalid Product UI Step Update')
  datadogRum.addError(error, {
    step,
  })
}

// Helper script is supposed to do redirects
// So partner sites aren't logged in a little iframe
export const captureWarningForBackupRedirect = () => {
  const error = new Error('Embed had to Redirect Itself')
  datadogRum.addError(error, undefined)
}

export const captureSpendingCategoriesRequestError = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Offer Augmentation Spending Categories Request Failed',
  })
}

export const captureOfferRedirectUrlParseUuidError = (url: string) => {
  const error = new Error('Offer URL did not contain a UUID')
  datadogRum.addError(error, {
    url,
  })
}

export const captureSessionInitFailedWarning = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'Session init failed',
  })
}

export const captureLocalStorageAccessWarning = (error: ErrorType) => {
  datadogRum.addError(error, {
    message: 'localStorage access denied',
  })
}
